import {inject, injectable} from "inversify";
import {action, configure, makeObservable, observable} from "mobx";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {translate} from "@dropDesk/storage/i18n/translate_helper";
import {
    GetCompanyBySearchPanelUseCase
} from "@dropDesk/domain/use_case/panel_adm/get_company_by_search_panel.usecase";
import {CompanyEntity} from "@dropDesk/domain/entities/company/company.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {RemoveCompanyCachePanelUseCase} from "@dropDesk/domain/use_case/panel_adm/remove_company_cache_panel.usecase";
import {ChatConnectionStatePanelUseCase} from "@dropDesk/domain/use_case/panel_adm/chat_connection_state_panel.usecase";
import {ChatIntegrationConnectionEntity} from "@dropDesk/domain/entities/chat/chat_integration_connection.entity";
import {ListPaymentsPanelUseCase} from "@dropDesk/domain/use_case/panel_adm/list_payments_panel.usecase";
import {RefundPaymentPanelUseCase} from "@dropDesk/domain/use_case/panel_adm/refund_payment_panel.usecase";
import {ExternallyPayPanelUseCase} from "@dropDesk/domain/use_case/panel_adm/externally_pay_payment_panel.usecase";
import {ListPaginationEntity} from "@dropDesk/domain/entities/common/list_pagination.entity";
import {PaymentEntity} from "@dropDesk/domain/entities/payment/payment.entity";
import {sleep} from "@dropDesk/utils/helpers/common";
import {
    AllowSubscriptionUpgradePanelUseCase
} from "@dropDesk/domain/use_case/panel_adm/allow_subscription_upgrade_panel.usecase";

configure({
    enforceActions: "always",
});

@injectable()
export class PanelAdmController {

    private readonly _getCompanyBySearchPanelUseCase: GetCompanyBySearchPanelUseCase;
    private readonly _removeCompanyCachePanelUseCase: RemoveCompanyCachePanelUseCase;
    private readonly _allowSubscriptionUpgradePanelUseCase: AllowSubscriptionUpgradePanelUseCase;
    private readonly _chatConnectionStatePanelUseCase: ChatConnectionStatePanelUseCase;
    private readonly _listPaymentsPanelUseCase: ListPaymentsPanelUseCase;
    private readonly _refundPaymentPanelUseCase: RefundPaymentPanelUseCase;
    private readonly _externallyPayPanelUseCase: ExternallyPayPanelUseCase;

    constructor(
        @inject(GetCompanyBySearchPanelUseCase) getCompanyBySearchPanelUseCase: GetCompanyBySearchPanelUseCase,
        @inject(RemoveCompanyCachePanelUseCase) removeCompanyCachePanelUseCase: RemoveCompanyCachePanelUseCase,
        @inject(ChatConnectionStatePanelUseCase) chatConnectionStatePanelUseCase: ChatConnectionStatePanelUseCase,
        @inject(ListPaymentsPanelUseCase) listPaymentsPanelUseCase: ListPaymentsPanelUseCase,
        @inject(RefundPaymentPanelUseCase) refundPaymentPanelUseCase: RefundPaymentPanelUseCase,
        @inject(ExternallyPayPanelUseCase) externallyPayPanelUseCase: ExternallyPayPanelUseCase,
        @inject(AllowSubscriptionUpgradePanelUseCase) allowSubscriptionUpgradePanelUseCase: AllowSubscriptionUpgradePanelUseCase,
    ) {
        makeObservable(this);
        this._getCompanyBySearchPanelUseCase = getCompanyBySearchPanelUseCase;
        this._removeCompanyCachePanelUseCase = removeCompanyCachePanelUseCase;
        this._chatConnectionStatePanelUseCase = chatConnectionStatePanelUseCase;
        this._listPaymentsPanelUseCase = listPaymentsPanelUseCase;
        this._refundPaymentPanelUseCase = refundPaymentPanelUseCase;
        this._externallyPayPanelUseCase = externallyPayPanelUseCase;
        this._allowSubscriptionUpgradePanelUseCase = allowSubscriptionUpgradePanelUseCase;
    }

    table = new ListPaginationEntity<PaymentEntity>({
        pages: 0,
        page: 0,
        limit: 5,
        totalRows: 0,
        data: observable.array([])
    });

    @observable
    search?: string = '';

    @observable
    loadingMessage?: string | null = null;

    @observable
    loading = false;

    @observable
    company?: CompanyEntity = undefined;

    @observable
    userMain?: UserEntity = undefined;

    @observable
    chatConnection?: ChatIntegrationConnectionEntity = undefined;

    @observable
    tablePayments: ListPaginationEntity<PaymentEntity> = this.table;

    @observable
    pagesData: Record<number, PaymentEntity[]> = {};

    @action
    setLoadingMessage(message?: string | null) {
        this.loadingMessage = message;
    }

    @action
    startLoading(loadingMessage?: string | null) {
        this.setLoadingMessage(loadingMessage);
        this.loading = true;
    }

    @action
    stopLoading() {
        this.loading = false;
        this.setLoadingMessage(null);
    }

    @action
    setSearch(value: string) {
        this.search = value.replace(/\D/g, "");
    }

    @action
    setCompany(company: CompanyEntity) {
        this.company = company;
    }

    @action
    setUserMain(user: UserEntity) {
        this.userMain = user;
    }

    @action
    setChatConnection(value: ChatIntegrationConnectionEntity) {
        this.chatConnection = value;
    }

    @action
    setTableInvoices(value: ListPaginationEntity<PaymentEntity>) {
        this.tablePayments = value;
        this.pagesData[this.tablePayments.page] = this.tablePayments.data;
    }

    @action
    getDataFromPage = async (page: number): Promise<void> => {
        if (this.pagesData[page]) {
            this.setTableInvoices(this.tablePayments.copyWith({
                ...this.tablePayments,
                page: page,
                data: this.pagesData[page],
            }))

        } else {
            this.setTableInvoices(this.tablePayments.copyWith({
                ...this.tablePayments,
                page: page,
            }))
            return this.listPayments();
        }
    }

    @action
    listPayments = async (): Promise<void> => {
        try {
            if(this.company){
                this.startLoading();
                const response = await this._listPaymentsPanelUseCase.call(
                    this.tablePayments.page,
                    this.tablePayments.limit,
                    this.company.id,
                    undefined
                );
                this.setTableInvoices(response)
                this.stopLoading();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    };

    @action
    getCompany = async (): Promise<void> => {
        try {
            if (this.search) {
                this.startLoading('panel.loadingCompany');
                const {company, user} = await this._getCompanyBySearchPanelUseCase.call(this.search);
                this.setCompany(company);
                this.setUserMain(user);
                await this.chatConnectionState();
                await this.listPayments();
                this.stopLoading();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    };

    @action
    removeCache = async (onSuccess: (key: string) => void): Promise<void> => {
        try {
            if (this.company) {
                this.startLoading('panel.removeCompanyCache');
                await this._removeCompanyCachePanelUseCase.call(this.company.id);
                this.stopLoading();
                onSuccess("panel.success.removeCompanyCache");
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    allowSubscriptionUpgrade = async (onSuccess: (key: string) => void): Promise<void> => {
        try {
            if (this.company) {
                this.startLoading('panel.allowSubscriptionUpgrade');
                await this._allowSubscriptionUpgradePanelUseCase.call(this.company.id, this.company.newSubs.id);
                this.stopLoading();
                onSuccess("panel.success.allowSubscriptionUpgrade");
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    externallyPay = async (payment: PaymentEntity, onSuccess: (key: string) => void): Promise<void> => {
        try {
            this.startLoading('panel.externallyPay');
            await this._externallyPayPanelUseCase.call(payment.idExternal, payment.id);
            await sleep(3);
            await this.listPayments();
            this.stopLoading();
            onSuccess("panel.success.externallyPay");
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    refund = async (payment: PaymentEntity, onSuccess: (key: string) => void): Promise<void> => {
        try {
            this.startLoading('panel.refund');
            await this._refundPaymentPanelUseCase.call(payment.idExternal, payment.id);
            await sleep(3);
            await this.listPayments();
            this.stopLoading();
            onSuccess("panel.success.refund");
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    chatConnectionState = async (): Promise<void> => {
        try {
            if (this.company) {
                const response = await this._chatConnectionStatePanelUseCase.call(this.company.id);
                this.setChatConnection(response);
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

}
